import { clone, includes, isDate, isNumber, join, map, replace, slice, trim } from 'lodash'
import { format, fromUnixTime, parseISO } from 'date-fns'
import FILESIZE from '@/constants/filesize'
import type { paginationMeta } from '@/services/api/client'
import { PUBLIC_SUBDOMAINS } from '@/constants/routes'

/**
 * Format human-readable date
 */
const formatDate = (date?: string | number | Date) => {
    if (!date) {
        return ``
    }

    const formatString = `MMMM do, yyyy`

    if (isNumber(date)) {
        return format(fromUnixTime(date), formatString)
    }

    if (isDate(date)) {
        return format(date, formatString)
    }

    return format(new Date(date), formatString)
}

/**
 * Parse a calendar-date value (e.g. a birthday) as a *local* date,
 * ignoring any time/timezone component.
 *
 * Birthdays are calendar dates with no timezone meaning, but the API may
 * return them either as a date-only string ("1990-05-10") or as a
 * UTC-midnight timestamp ("1990-05-10T00:00:00.000Z"). Both `new Date()`
 * and date-fns `parseISO()` applied to those would render a day early for
 * anyone west of UTC (e.g. May 9 in America/Los_Angeles). Slicing to the
 * date portion first and parsing that as a local date avoids the shift.
 */
const parseDateOnly = (value: string) => parseISO(value.slice(0, 10))

/**
 * Format a calendar-date value (e.g. a birthday) as a human-readable date,
 * without the UTC-midnight off-by-one-day shift. See {@link parseDateOnly}.
 */
const formatDateOnly = (value?: string | null) => {
    if (!value) {
        return ``
    }

    return format(parseDateOnly(value), `MMMM do, yyyy`)
}

/**
 * Format an epoch-seconds calendar date (e.g. a custom `date` profile field) as
 * a human-readable date, in the same style as {@link formatDateOnly}.
 *
 * The api serialises `date` profile fields as epoch seconds taken from a bare
 * `Y-m-d` under an app timezone of UTC, so the value is always UTC midnight.
 * Reading it back with local getters — or with `fromUnixTime`, which builds a
 * local Date — renders a day early for anyone west of UTC. Converting via the
 * UTC date portion first avoids that shift. See {@link parseDateOnly}.
 */
const formatEpochDateOnly = (value?: string | number | null) => {
    if (!value) {
        return ``
    }

    const seconds = Number(value)

    // A date field with several stored values arrives comma-joined; there is no
    // single date to show, so show nothing rather than a wrong one.
    if (!Number.isFinite(seconds) || seconds === 0) {
        return ``
    }

    return formatDateOnly(new Date(seconds * 1000).toISOString().slice(0, 10))
}

/**
 * Format human-readable duration
 */
const formatDuration = (seconds = 0, minutes = 0, hours = 0) => {
    const remainingSeconds = seconds % 60

    switch (true) {
        case seconds === 0 && minutes === 0 && hours === 0:
            return ''
        case hours > 0 && minutes > 0:
            return `${hours}h ${minutes}m`
        case hours > 0:
            return `${hours}h`
        case minutes > 0 && remainingSeconds > 0:
            return `${minutes}m ${remainingSeconds}s`
        case minutes > 0:
            return `${minutes}m`
        default:
            return `${remainingSeconds}s`
    }
}

/**
 * Format human-readable file size
 */
const formatFileSize = (bytes: number) => {
    if (!bytes) {
        return '0 Bytes'
    }

    const sizes = [`Bytes`, `KB`, `MB`, `GB`, `TB`]
    const calculated = Math.floor(Math.log(bytes) / Math.log(FILESIZE.Kilobyte()))

    return `${parseFloat((bytes / Math.pow(FILESIZE.Kilobyte(), calculated)).toFixed(2))} ${sizes[calculated]}`
}

/**
 * Insert an item into an array at set index.
 */
const insertAtIndex = (arr: unknown[] = [], item: unknown, index: number) => [
    ...slice(arr, 0, index),
    item,
    ...slice(arr, index)
]

/**
 * Joins an array with commas & ampersand (or a custom conjunctive, such as 'and')
 */
const joinList = (list: string[], conjunctive = `&`) => {
    const newList = clone(list)
    const last = newList.pop()

    return newList.length ? `${join(newList, `, `)}, ${conjunctive} ${last}` : last
}

/**
 * Returns a new array with all the trailing/leading whitespace removed from each item.
 * Also replaces multiple spaces with a single space.
 */
const trimArrayWhitespace = (arr: string[]) => map(arr, item => replace(trim(item), /\s+/g, ` `))

/*
 * This is used exclusively by transformers
 */
const transformDate = (value?: string | Date | null) => {
    if (!value) {
        return value
    }

    return typeof value === 'string' ? parseISO(value) : value.toISOString()
}

/**
 * Gets the next page param for useInfiniteQuery
 */
const getNextPageParam = (lastPage: { meta: paginationMeta }): undefined | number => {
    if (lastPage.meta.current_page === lastPage.meta.last_page) {
        return undefined
    }
    return lastPage.meta.current_page + 1
}

const getSubdomain = () => window.location.hostname.split(`.`)[0]

const isPublicSubdomain = () => includes(PUBLIC_SUBDOMAINS, getSubdomain())

/**
 * Encodes filename given a filepath
 */
const encodeFileName = (url: string) => {
    const lastSlashIndex = url.lastIndexOf('/')
    const directoryPath = url.substring(0, lastSlashIndex + 1)
    const filename = url.substring(lastSlashIndex + 1)

    const filenameEncoded = encodeURIComponent(filename)
    return directoryPath + filenameEncoded
}

export {
    encodeFileName,
    formatDate,
    formatDateOnly,
    formatDuration,
    formatEpochDateOnly,
    parseDateOnly,
    formatFileSize,
    getNextPageParam,
    getSubdomain,
    insertAtIndex,
    isPublicSubdomain,
    joinList,
    transformDate,
    trimArrayWhitespace
}
